// Phase JB.1 — useGapReport hook.
//
// Returns the latest gap_reports row for the auth user, plus a
// realtime subscription so a fresh weekly report appears live.
// Anon → returns { report: null, loading: false }.

import { useEffect, useId, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";

export interface GapReport {
  report_id: string;
  user_id: string;
  week_start: string;
  foregone_units: number;
  signals_missed: number;
  signals_won_missed: number;
  generated_at: string;
}

export interface UseGapReportResult {
  report: GapReport | null;
  loading: boolean;
  error: string | null;
}

export function useGapReport(): UseGapReportResult {
  const { user } = useAuthSession();
  const instanceId = useId();
  const [report, setReport] = useState<GapReport | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setReport(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (sb.from("gap_reports" as never) as any)
      .select("report_id,user_id,week_start,foregone_units,signals_missed,signals_won_missed,generated_at")
      .eq("user_id", user.id)
      .order("week_start", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then((res: { data: GapReport | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (res.error) {
          setError(res.error.message);
        } else {
          setReport(res.data ?? null);
        }
        setLoading(false);
      });

    const channel = sb
      .channel(`gap_reports:${user.id}:${instanceId}`)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "postgres_changes" as any,
        { event: "INSERT", schema: "public", table: "gap_reports", filter: `user_id=eq.${user.id}` },
        (payload: { new: GapReport }) => {
          setReport((prev) => {
            if (!prev) return payload.new;
            return payload.new.week_start > prev.week_start ? payload.new : prev;
          });
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void sb.removeChannel(channel);
    };
  }, [user, instanceId]);

  return { report, loading, error };
}
